import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill, Input } from "../../components/common/Primitives";
import { Coins, AlertTriangle, RefreshCcw, Eye, EyeOff, ShieldCheck, PencilLine, TrendingUp } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { AdminDDReportGenerator } from "./DDReports";

const SOURCE_META = {
  api_fetched: { label: "API", tone: "success" },
  document_parsed: { label: "PARSED", tone: "gold" },
  founder_declared: { label: "FOUNDER", tone: "warn" },
  admin_entered: { label: "ADMIN", tone: "success" },
  admin_verified: { label: "VERIFIED", tone: "success" },
  conflicting: { label: "CONFLICT", tone: "danger" },
  mca_derived: { label: "MCA", tone: "success" },
  unverified: { label: "UNVERIFIED", tone: "muted" },
};

const SEVERITY_TONE = { high: "danger", medium: "warn", low: "muted" };
const LABEL_TONE = {
  "Investor Ready": "success", "Almost Ready": "gold", "Needs Review": "warn",
  "High Risk": "danger", "Insufficient Data": "muted",
};

const FIELD_LABELS = {
  monthly_revenue: "Monthly Revenue", annual_revenue: "Annual Revenue", arr: "ARR",
  gross_profit: "Gross Profit", gross_margin_pct: "Gross Margin %", ebitda: "EBITDA",
  ebitda_margin_pct: "EBITDA Margin %", pat: "PAT / Net Profit",
  monthly_burn: "Monthly Burn", runway_months: "Runway (mo)",
  employee_cost: "Employee Cost", marketing_cost: "Marketing", tech_cost: "Tech",
  authorised_capital: "Authorised Capital", paid_up_capital: "Paid-Up",
  net_worth: "Net Worth", debt: "Debt", cash_and_bank: "Cash & Bank",
  trade_receivables: "Receivables", trade_payables: "Payables",
  cac: "CAC", ltv: "LTV", churn_rate_pct: "Churn %", retention_rate_pct: "Retention %",
  current_valuation_expectation: "Valuation Expectation",
  promoter_holding_pct: "Promoter %", external_investor_holding_pct: "External %",
  esop_pool_pct: "ESOP %", amount_raised_till_date: "Total Raised",
};

const GROUP_LABELS = {
  revenue: "Revenue", profitability: "Profitability", cost_and_burn: "Cost & Burn",
  balance_sheet: "Balance Sheet", unit_economics: "Unit Economics", funding: "Funding",
};


export function AdminFinancialReviewList() {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/financial-review${label ? `?label=${encodeURIComponent(label)}` : ""}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [label]);

  return (
    <DashboardShell title="Financial Review" subtitle="ADMIN · DILIGENCE ENGINE">
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-4">
        <select value={label} onChange={(e) => setLabel(e.target.value)} className="border border-navy/15 rounded-md px-3 py-1.5 text-sm font-heading" data-testid="admin-fr-label-filter">
          <option value="">All labels</option>
          {Object.keys(LABEL_TONE).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="text-xs font-mono text-ink-muted">{items.length} startups</div>
      </div>
      <Card testId="admin-fr-table-card" className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-fr-table">
          <thead className="bg-navy/[0.04] border-b border-navy/10">
            <tr>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Startup</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Label</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Overall</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Investor Ready</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Flags</th>
              <th className="text-left px-4 py-3 font-heading text-xs uppercase tracking-wider text-ink-muted">Fields</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.startup_id} className="border-b border-navy/[0.06] hover:bg-navy/[0.02]" data-testid={`admin-fr-row-${it.startup_id}`}>
                <td className="px-4 py-3">
                  <div className="font-heading text-ink">{it.startup_name}</div>
                  <div className="text-xs text-ink-muted">{it.founder_email}</div>
                </td>
                <td className="px-4 py-3"><Pill tone={LABEL_TONE[it.label] || "muted"}>{it.label}</Pill></td>
                <td className="px-4 py-3 font-mono">{it.overall_score ?? "—"}</td>
                <td className="px-4 py-3 font-mono">{it.investor_readiness_score ?? "—"}</td>
                <td className="px-4 py-3">
                  {it.red_flag_counts.high > 0 && <Pill tone="danger">{it.red_flag_counts.high} H</Pill>}
                  {it.red_flag_counts.medium > 0 && <Pill tone="warn">{it.red_flag_counts.medium} M</Pill>}
                  {it.red_flag_counts.low > 0 && <Pill tone="muted">{it.red_flag_counts.low} L</Pill>}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">{it.fields_captured} / 40+ · {it.extractions_count} extractions</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/admin/financial-review/${it.startup_id}`} className="text-[#8A6600] font-heading text-sm hover:underline" data-testid={`admin-fr-open-${it.startup_id}`}>Open →</Link>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted">No startups match this filter.</td></tr>}
          </tbody>
        </table>
      </Card>
    </DashboardShell>
  );
}


export function AdminFinancialReviewDetail() {
  const { startupId } = useParams();
  const [data, setData] = useState(null);
  const [editValue, setEditValue] = useState({ field: null, value: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/financial-review/${startupId}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [startupId]);

  const verifyField = async (field) => {
    setBusy(true);
    try {
      await api.post(`/admin/financial-review/${startupId}/verify-field`, { field, verified: true });
      toast.success(`Verified ${FIELD_LABELS[field] || field}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleVis = async (field, currentlyVisible) => {
    setBusy(true);
    try {
      await api.post(`/admin/financial-review/${startupId}/toggle-visibility`, { field, investor_visible: !currentlyVisible });
      toast.success(`Investor visibility ${!currentlyVisible ? "enabled" : "hidden"} for ${FIELD_LABELS[field] || field}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const override = async () => {
    if (!editValue.field || editValue.value === "") return;
    setBusy(true);
    try {
      await api.post(`/admin/financial-review/${startupId}/override-field`, {
        field: editValue.field,
        value: Number(editValue.value),
        reason: editValue.reason,
      });
      toast.success(`Overrode ${FIELD_LABELS[editValue.field] || editValue.field}.`);
      setEditValue({ field: null, value: "", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recompute = async () => {
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/admin/financial-review/${startupId}/recompute-flags`);
      toast.success(`Recomputed. ${resp.red_flags.length} red flag(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <DashboardShell title="Financial Review"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  const fp = data.financial_profile || { fields: {}, field_sources: {}, red_flags: [], scores: {}, extractions: [] };
  const scores = fp.scores || {};
  const flags = fp.red_flags || [];

  return (
    <DashboardShell title={data.startup.startup_name || "Financial Review"} subtitle="ADMIN · DILIGENCE DETAIL" actions={
      <div className="flex gap-2">
        <Button variant="ghost" onClick={recompute} disabled={busy} data-testid="admin-fr-recompute-btn"><RefreshCcw size={12} className="inline mr-1" /> Recompute Flags</Button>
        <Link to="/admin/financial-review" className="text-sm text-[#8A6600] hover:underline self-center">← Back</Link>
      </div>
    }>
      <Toaster richColors position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          {Object.entries(data.field_groups).map(([groupKey, fields]) => (
            <Card key={groupKey} testId={`admin-fr-group-${groupKey}`}>
              <div className="overline mb-3 flex items-center gap-2"><Coins size={14} /> {GROUP_LABELS[groupKey]}</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-navy/10">
                    <th className="text-left py-1 font-heading uppercase tracking-wider text-ink-muted">Field</th>
                    <th className="text-right py-1 font-heading uppercase tracking-wider text-ink-muted">Value</th>
                    <th className="text-center py-1 font-heading uppercase tracking-wider text-ink-muted">Source</th>
                    <th className="text-center py-1 font-heading uppercase tracking-wider text-ink-muted">Investor View</th>
                    <th className="text-right py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => {
                    const val = fp.fields?.[f];
                    const src = fp.field_sources?.[f];
                    const meta = SOURCE_META[src?.source] || SOURCE_META.unverified;
                    const editing = editValue.field === f;
                    return (
                      <tr key={f} className="border-b border-navy/[0.05]" data-testid={`admin-fr-field-${f}`}>
                        <td className="py-1.5 text-ink">{FIELD_LABELS[f] || f}</td>
                        <td className="py-1.5 text-right font-mono">
                          {editing ? (
                            <input type="number" value={editValue.value} onChange={(e) => setEditValue({ ...editValue, value: e.target.value })} className="w-24 border-b border-gold text-right py-0.5 outline-none" data-testid={`admin-fr-edit-input-${f}`} />
                          ) : (val != null ? Number(val).toLocaleString("en-IN") : "—")}
                          {src?.source === "conflicting" && src.extracted_value != null && (
                            <div className="text-[10px] text-rose-700">doc: {src.extracted_value.toLocaleString("en-IN")}</div>
                          )}
                        </td>
                        <td className="py-1.5 text-center">{src && <Pill tone={meta.tone}>{meta.label}{src.confidence ? ` ${src.confidence}%` : ""}</Pill>}</td>
                        <td className="py-1.5 text-center">
                          <button onClick={() => toggleVis(f, src?.investor_visible)} className="inline-flex items-center gap-1 text-xs" data-testid={`admin-fr-vis-${f}`}>
                            {src?.investor_visible ? <><Eye size={12} className="text-emerald-600" /><span className="text-emerald-700">Visible</span></> : <><EyeOff size={12} className="text-ink-muted" /><span className="text-ink-muted">Hidden</span></>}
                          </button>
                        </td>
                        <td className="py-1.5 text-right">
                          {editing ? (
                            <div className="flex gap-1 justify-end">
                              <button onClick={override} disabled={busy} className="text-emerald-700 text-xs hover:underline" data-testid={`admin-fr-save-${f}`}>Save</button>
                              <button onClick={() => setEditValue({ field: null, value: "", reason: "" })} className="text-ink-muted text-xs hover:underline">✕</button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              {val != null && !src?.verified && <button onClick={() => verifyField(f)} className="p-1 hover:bg-emerald-500/10 rounded" title="Verify" data-testid={`admin-fr-verify-${f}`}><ShieldCheck size={12} className="text-emerald-700" /></button>}
                              <button onClick={() => setEditValue({ field: f, value: val || "", reason: "" })} className="p-1 hover:bg-gold/10 rounded" title="Override" data-testid={`admin-fr-edit-${f}`}><PencilLine size={12} className="text-[#8A6600]" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {editValue.field && fields.includes(editValue.field) && (
                <Input label="Override reason (internal note)" value={editValue.reason} onChange={(e) => setEditValue({ ...editValue, reason: e.target.value })} testId={`admin-fr-reason-${editValue.field}`} className="mt-2" />
              )}
            </Card>
          ))}

          {(fp.extractions || []).length > 0 && (
            <Card testId="admin-fr-extractions-card">
              <div className="overline mb-3">Extraction History ({fp.extractions.length})</div>
              <div className="space-y-1 text-xs">
                {fp.extractions.slice().reverse().slice(0, 10).map((e) => (
                  <div key={e.extraction_id} className="border-b border-navy/[0.05] pb-1.5">
                    <div className="flex justify-between">
                      <div className="font-mono">{e.doc_type} · {e.filename}</div>
                      <div className="text-ink-muted">{e.created_at?.slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <div className="text-ink-muted">{Object.keys(e.extracted).length} fields · {(e.conflicts || []).length} conflicts · by {e.run_by}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <AdminDDReportGenerator
            startupId={startupId}
            onGenerated={(rep) => {
              toast.success(`Report ${rep.report_id} ready. Opening…`);
              setTimeout(() => { window.location.href = `/admin/dd-reports/${startupId}/${rep.report_id}`; }, 800);
            }}
          />
          <Card testId="admin-fr-scores-card">
            <div className="overline mb-3 flex items-center gap-2"><TrendingUp size={14} /> Readiness</div>
            <div className="mb-3">
              <div className="text-4xl font-display text-ink">{scores.overall_health_score ?? "—"}</div>
              <Pill tone={LABEL_TONE[scores.label] || "muted"} data-testid="admin-fr-label-pill">{scores.label || "Insufficient Data"}</Pill>
            </div>
            <div className="space-y-1.5 text-xs">
              <ScoreRow label="Company Verification" v={scores.company_verification_score} />
              <ScoreRow label="Financial Readiness" v={scores.financial_readiness_score} />
              <ScoreRow label="Documentation" v={scores.documentation_score} />
              <ScoreRow label="Compliance" v={scores.compliance_score} />
              <ScoreRow label="Investor Readiness" v={scores.investor_readiness_score} highlight />
            </div>
          </Card>

          <Card testId="admin-fr-flags-card">
            <div className="overline mb-3 flex items-center gap-2"><AlertTriangle size={14} /> Red Flags ({flags.length})</div>
            {flags.length === 0 ? (
              <div className="text-sm text-ink-muted italic">No red flags.</div>
            ) : (
              <div className="space-y-2">
                {flags.map((f, i) => (
                  <div key={f.flag_id || i} className="border-l-2 pl-3 py-1" style={{ borderColor: f.severity === "high" ? "#EF4444" : f.severity === "medium" ? "#F59E0B" : "#9CA3AF" }} data-testid={`admin-fr-flag-${f.code}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <Pill tone={SEVERITY_TONE[f.severity]}>{f.severity}</Pill>
                      <span className="text-[10px] font-mono text-ink-muted">{f.category}</span>
                    </div>
                    <div className="text-xs text-ink">{f.message}</div>
                    {f.suggestion && <div className="text-[10px] text-ink-muted italic mt-0.5">→ {f.suggestion}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardShell>
  );
}

function ScoreRow({ label, v, highlight }) {
  const value = v ?? 0;
  const color = value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-gold" : value >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className={highlight ? "font-heading text-ink" : "text-ink-muted"}>{label}</span>
        <span className="font-mono">{v ?? "—"}</span>
      </div>
      <div className="h-1 bg-navy/[0.06] rounded-full mt-0.5 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}
